import { countryName, signalWords } from '@/ui/words';
import { BuyerAnswers } from '@/validation/Demand';
import { Link, useNavigate, useParams } from 'react-router';
import { failureOf } from '@/api/errors';
import { useBuyers, useIntros, useOurIndex, useSignals } from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import { ToolLabel } from '@/app/technical';
import { AgentBrief } from '@/ui/brief';
import { Button, Skeleton } from '@/ui/primitives';
import { FactList, RecordHeader, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { CONFIDENCE_CHIP } from '@/validation/Validation';
import { googleMapsUrl, indexEntryName, shortDate } from './words';
import { STAGE_WORDS } from '@/outreach/words';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { explainSignal, readerOf } from './explain';
import { explainBuyer, explainIndexEntry, explainIntro } from './explain-follow';

/**
 * The record pages the prototype has and the lists opened inline (SG-06):
 * a buyer, an introduction, a signal, a row of our index. Each is one row
 * of its department's list, read by id, with a "For agents" brief so the
 * side chat is told the id and nothing else (§11). Nothing here writes: a
 * buyer is approved from Buyers under the company's token, an
 * introduction is answered by the colleague asked, a signal is what was
 * seen, and an index row is what a plan found.
 */

const NONE = { kind: 'refused', status: 404 } as const;

/** A buyer (S:18): the role at the company, how to reach them in kinds, and what they are to us. */
export function BuyerPage({ recordId }: { recordId?: string } = {}) {
  const params = useParams();
  const id = recordId ?? params.id ?? '';
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const q = useBuyers(me.department);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={8} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const b = q.data.buyers.find((x) => x.id === id);
  if (!b) return <div className="p-6"><Refusal failure={{ ...NONE, code: 'no_such_buyer', detail: 'No such buyer in this department.' }} /></div>;
  const relationship = b.company?.relationship ?? 'unapproved';
  const approved = relationship !== 'unapproved';
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-8 p-6 phone:p-4" data-testid="buyer-page">
      <RecordHeader
        kicker="Buyer"
        title={b.erased ? 'Erased' : b.persona ? b.roleTitle : `${b.name ?? '—'} · ${b.roleTitle}`}
        subtitle={`${b.companyName} · ${b.marketCode}`}
        chips={[{ label: relationship.toUpperCase(), tone: approved ? 'accent' : 'neutral' }, CONFIDENCE_CHIP(b.confidence), ...(b.approved ? [{ label: 'APPROVED', tone: 'accent' as const }] : [])]}
        back={{ to: '/buyers', label: 'Buyers' }}
        actions={
          <>
            {b.company && <Button onClick={() => void navigate(`/companies/${b.company!.id}`)}>The company</Button>}
            {b.prospectId && <Button onClick={() => void navigate(`/prospects/${b.prospectId}`)}>The prospect</Button>}
          </>
        }
      />
      <Explainer startOpen context={explainBuyer(b, who, readerOf(me))} />
      <FactList
        facts={[
          ['Company', b.company ? <Link key="c" className="text-accent underline" to={`/companies/${b.company.id}`}>{b.company.name}</Link> : `${b.companyName} — not in our records yet`],
          ['What they are to us', approved ? relationship : 'Unapproved — a superadmin approves the company first, then the buyer'],
          ['Type', b.companyType],
          ['Buyer', b.erased ? 'Their details were destroyed' : b.persona ? 'A role; no name is on record' : b.name],
          ['Approved', b.approved ? 'Yes — they may be written to' : 'Not yet'],
          ['Reach', [b.linkedin && 'LinkedIn', b.phone && 'phone', b.email && 'email', b.switchboard && 'switchboard'].filter(Boolean).join(' · ') || 'No way to reach them on record'],
          ['Email', b.email],
          ['Phone', b.phone],
          ['LinkedIn', b.linkedin],
          ['Switchboard', b.switchboard],
          ['Supplied by today', b.suppliedBy],
          ['Website', b.website],
          ['Where it came from', b.source],
          ['How sure', b.confidence],
        ]}
      />
      <AgentBrief lines={[['buyer', b.id], ['market', b.marketCode], ['company', b.company?.id], ['relationship', relationship], ['approved', b.approved], ['persona', b.persona], ['prospect', b.prospectId], ['confidence', b.confidence]]} />
      <BuyerAnswers buyerId={b.id} />
    </div>
  );
}

/** An introduction (R1): asked of a colleague, with the message, and what came of it in their words. */
export function IntroductionPage() {
  const { id = '' } = useParams();
  const me = useMeOrThrow();
  const q = useIntros(me.department, id);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const i = q.data.intros.find((x) => x.id === id);
  if (!i) return <div className="p-6"><Refusal failure={{ ...NONE, code: 'no_such_intro', detail: 'No such introduction in this department.' }} /></div>;
  const chip = i.state === 'made' ? { label: 'MADE', tone: 'accent' as const } : i.state === 'declined' ? { label: 'DECLINED', tone: 'danger' as const } : { label: 'ASKED', tone: 'warning' as const };
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-8 p-6 phone:p-4" data-testid="introduction-page">
      <RecordHeader kicker="Introduction" title={i.prospect ?? 'Name not written down yet'} subtitle={`Asked of ${i.via} · ${i.marketCode}`} chips={[chip]} back={{ to: '/introductions', label: 'Introductions' }} />
      <Explainer startOpen context={explainIntro(i, who, readerOf(me))} />
      <FactList
        facts={[
          ['Prospect', <Link key="p" className="text-accent underline" to={`/prospects/${i.prospectId}`}>{i.prospect ?? 'Open the prospect'}</Link>],
          ['Asked of', i.via],
          ['Asked', when(i.askedAt)],
          ['Message', i.message ? `“${i.message}”` : 'None — the colleague was asked in their own words'],
          ['Outcome', i.state === 'asked' ? 'Waiting on the colleague; the cold sequence waits with it' : `${i.state === 'made' ? 'Introduced' : 'Could not'}${i.answeredAt ? ` ${when(i.answeredAt)}` : ''}${i.outcome ? ` — “${i.outcome}”` : ''}`],
          ['What follows', i.state === 'made' ? 'The cold sequence does not start; the thread is theirs to continue' : i.state === 'declined' ? 'The cold sequence starts in its next send window' : 'Nothing until they answer'],
        ]}
      />
      <p className="m-0 max-w-[64ch] text-dense text-meta">Only the colleague asked answers it, from Introductions or their day. Nothing here writes to the prospect.</p>
      <AgentBrief lines={[['intro', i.id], ['prospect', i.prospectId], ['market', i.marketCode], ['via', i.viaHuman], ['state', i.state], ['has_message', Boolean(i.message)]]} />
    </div>
  );
}

/** A signal (PR-13, ADR-0141): what was seen, of whom, where it was read, and the points it adds. */
export function SignalPage({ recordId }: { recordId?: string } = {}) {
  const params = useParams();
  const id = recordId ?? params.id ?? '';
  const me = useMeOrThrow();
  const q = useSignals(me.department);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const s = q.data.signals.find((x) => x.id === id);
  if (!s) return <div className="p-6"><Refusal failure={{ ...NONE, code: 'no_such_signal', detail: 'No such signal in this department.' }} /></div>;
  const contact = s.source?.match(/^contact:([0-9a-f-]{36})$/)?.[1];
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-8 p-6 phone:p-4" data-testid="signal-page">
      <RecordHeader kicker="Signal" title={s.said} subtitle={`${signalWords(s.kind)} · ${countryName(s.marketCode)} · seen ${shortDate(s.seenOn)}`} chips={[{ label: `+${s.points}`, tone: s.points > 0 ? 'accent' : 'neutral' }]} back={{ to: '/signals', label: 'Signals' }} />
      <Explainer startOpen context={explainSignal(s, who, readerOf(me))} />
      <FactList
        facts={[
          ['Subject', s.prospectId ? <Link key="p" className="text-accent underline" to={`/prospects/${s.prospectId}`}>{s.prospect ?? 'Open the prospect'}</Link> : contact ? <Link key="c" className="text-accent underline" to={`/contacts/${contact}`}>A contact we know</Link> : 'The market'],
          ['Kind', signalWords(s.kind)],
          ['What was seen', s.said],
          ['Points', s.points === 0 ? '0 — a reason not to write, never points' : `+${s.points} to the fit`],
          ['Source', contact ? 'A contact we know, by id' : s.sourceId ? 'One of the market’s own sources' : (s.source ?? 'Read off the prospect’s own site')],
          ['Read at', s.url ? <a key="u" href={s.url} target="_blank" rel="noreferrer noopener" className="text-accent underline">{s.url}</a> : null],
          ['Seen', shortDate(s.seenOn)],
        ]}
      />
      <p className="m-0 max-w-[64ch] text-dense text-meta">A signal is the words that were seen and where, never a model’s summary; a person reads the page at the link before acting on it.</p>
      <AgentBrief lines={[['signal', s.id], ['kind', s.kind], ['market', s.marketCode], ['prospect', s.prospectId], ['source', s.sourceId ?? s.source], ['points', s.points], ['seen_on', s.seenOn]]} />
    </div>
  );
}

/** A row of our index (B9): what a plan found, by which tool, and what it has saved since. A person stays sealed. */
export function IndexEntryPage() {
  const { id = '' } = useParams();
  const me = useMeOrThrow();
  const q = useOurIndex(me.department, id);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const e = q.data.entries.find((x) => x.id === id);
  if (!e) return <div className="p-6"><Refusal failure={{ ...NONE, code: 'no_such_entry', detail: 'No such row in this department’s index.' }} /></div>;
  const title = indexEntryName(e);
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-8 p-6 phone:p-4" data-testid="index-entry-page">
      <RecordHeader
        kicker="Our index"
        title={title}
        subtitle={e.kind === 'person' ? `At ${e.at ?? 'a place whose name is not written down yet'} · ${e.marketCode}` : e.marketCode}
        chips={[e.shredded ? { label: 'SHREDDED', tone: 'neutral' } : e.fresh ? { label: 'FRESH', tone: 'accent' } : { label: 'LOOK UP AGAIN', tone: 'warning' }]}
        back={{ to: '/our-index', label: 'Our index' }}
      />
      <Explainer startOpen context={explainIndexEntry(e, who, readerOf(me), { freshDays: q.data.freshDays, name: title })} />
      <FactList
        facts={[
          ['Kind', e.kind === 'place' ? 'A place' : 'A person, sealed under their own key; never named here'],
          [
            'On Google Maps',
            e.placeId ? (
              <a className="text-accent underline" href={googleMapsUrl(e.placeId)} target="_blank" rel="noopener noreferrer">
                See it on Google Maps
              </a>
            ) : null,
          ],
          [
            'Prospect',
            e.prospectId && e.kind === 'place' ? (
              <Link className="text-accent underline" to={`/prospects/${e.prospectId}`}>
                {e.stage ? STAGE_WORDS[e.stage as keyof typeof STAGE_WORDS] ?? e.stage : 'Open it'}
              </Link>
            ) : null,
          ],
          ['Why no name', e.kind === 'place' && !e.name ? 'Google lets us keep only a place’s id. Its name comes from its own website once that is read, and Google Maps shows it meanwhile.' : null],
          ['Found by', <ToolLabel id={e.sourceTool} />],
          ['First seen', when(e.firstSeen)],
          ['Refreshed', when(e.refreshedAt)],
          ['Lookups saved', `${e.saved} — each a tool call not paid for again`],
          ['State', e.shredded ? 'Shredded: the key is gone and nothing of them is readable' : e.fresh ? `Fresh: a plan asks here and calls no tool` : `Older than ${q.data.freshDays} days: the next plan that needs it calls the tool once and refreshes it`],
          ['Readable until', e.readableUntil ? `${when(e.readableUntil)} — then shredded, unless a person approves them` : null],
        ]}
      />
      <AgentBrief lines={[['index_entry', e.id], ['kind', e.kind], ['market', e.marketCode], ['source_tool', e.sourceTool], ['fresh', e.fresh], ['saved', e.saved], ['shredded', e.shredded]]} />
    </div>
  );
}
